import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Label } from "@progress/kendo-react-labels";
import { Button } from "@progress/kendo-react-buttons";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import { LoadingPanel } from "../../LoadingPanel";
import { ListView } from "@progress/kendo-react-listview";
import { Input } from "@progress/kendo-react-inputs";
import { Dialog } from "@progress/kendo-react-dialogs";
import { AdministratorVenueEventLogs } from "./VenueEventLogs";
import { AdministratorVenueEventStatuses } from "./VenueEventStatuses";
import { AdministratorVenueEventPerformers } from "./VenueEventPerformers";
import { AdministratorVenueEventTicketCounts } from "./VenueEventTicketCounts";
import { AdministratorVenueEventAlerts } from "../venueeventalert/VenueEventAlerts";
import { AdministratorVenueEventHiddenFromReports } from "./VenueEventHiddenFromReports";
import { AdministratorVenueEventTemporarilyHiddenFromReports } from "./VenueEventTemporarilyHiddenFromReports";
import { AdministratorVenueEventTicketTypes } from "./VenueEventTicketTypes";
import { AdministratorMatchVenueEvent } from "./MatchVenueEvent";
import { AdministratorVenueEventShorting } from "./VenueEventShorting";
import { AdministratorVenueEventBlockedSecondaryTicketingWebsites } from "./VenueEventBlockedSecondaryTicketingWebsites";

import {
    FormInput,
    FormCheckbox,
    FormDropDownList,
    FormNumericTextBox,
    FormAutoComplete,
} from "../../forms/form-components";

import './VenueEvent.scss';

export const AdministratorVenueEvent = (props) => {
    const dispatch = useDispatch();

    const changeTicketingWebsiteReference = useRef(null);
    const changeTicketingUrlReference = useRef(null);
    const changeTicketingEventIdReference = useRef(null);
    const changeVenueEventStatusTypeReference = useRef(null);
    const changeEventDateReference = useRef(null);
    const changeTaglineReference = useRef(null);
    const changeHeaderReference = useRef(null);
    const changeSubheaderReference = useRef(null);
    const changePurchaseTicketLimitReference = useRef(null);
    const changeMinimumAgeReference = useRef(null);
    const changeCapacityReference = useRef(null);
    const changeNotesReference = useRef(null);
    const changeHasLowTicketCountReference = useRef(null);
    const changeIsPurchaseRestrictedByLocationReference = useRef(null);
    const changeVenueNameReference = useRef(null);

    const [selectedTab, setSelectedTab] = useState(0);
    const [gettingEventDetails, setGettingEventDetails] = useState(false);

    const [selectedTicketingWebsiteId, setSelectedTicketingWebsiteId] = useState(null);
    const [enteredTicketingUrl, setEnteredTicketingUrl] = useState(null);

    const [venues, setVenues] = useState([]);
    const [venueName, setVenueName] = useState(null);
    const [areVenuesLoading, setAreVenuesLoading] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [eventParameterKey, setEventParameterKey] = useState(null);
    const [eventParameterValue, setEventParameterValue] = useState(null);
    const [eventParameters, setEventParameters] = useState([]);

    const autocompleteTimeout = useRef();
    const autocompleteTimeoutDelay = 500; //Milliseconds

    const handleSubmit = (dataItem) => {
        const venue = venues.find(element => element.currentName === venueName);

        if (!venue) {
            addNotificationWithTimeout(dispatch, "error", "You must select a venue");
            return;
        }

        dataItem.venueKey = venue.venueKey;
        dataItem.ticketingWebsiteId = dataItem.ticketingWebsite.ticketingWebsiteId;
        dataItem.venueEventStatusTypeId = dataItem.venueEventStatusType.venueEventStatusTypeId;
        dataItem.venueEventParameters = eventParameters;

        let fetchMethod, successMessage;

        if (!dataItem.venueEventKey) {
            //Create new venueEvent
            fetchMethod = "POST";
            successMessage = "Created item successfully";
        }
        else {
            //Update existing venueEvent
            fetchMethod = "PUT";
            successMessage = "Updated item successfully";
        }

        fetch("/api/administrator/venueevent",
            {
                method: fetchMethod,
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", successMessage);

                props.selectVenueEvent(result.venueEventKey);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const deleteVenueEvent = (venueEventKey) => {
        if (window.confirm("Are you sure you want to delete this item?") === false) {
            return;
        }

        const dataItem = {
            venueEventKey: venueEventKey
        }

        fetch("/api/administrator/venueevent",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted item successfully");

                props.cancel();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const getEventDetails = () => {
        if (!enteredTicketingUrl
            || !selectedTicketingWebsiteId) {
            addNotificationWithTimeout(dispatch, "error", "You must select a ticketing website and enter a ticketing Url.");
            return;
        }

        setGettingEventDetails(true);

        const dataItem = {
            url: enteredTicketingUrl,
            ticketingWebsiteId: selectedTicketingWebsiteId,
        }

        fetch("/api/general/event",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                changeTicketingEventIdReference.current.handleClick(result.id ?? "");
                changeTaglineReference.current.handleClick(result.tagline ?? "");
                changeHeaderReference.current.handleClick(result.header ?? "");
                changeSubheaderReference.current.handleClick(result.subheader ?? "");
                changePurchaseTicketLimitReference.current.handleClick(result.purchaseTicketLimit ?? null);
                changeMinimumAgeReference.current.handleClick(result.minimumAge ?? null);
                changeCapacityReference.current.handleClick(result.capacity ?? null);
                changeNotesReference.current.handleClick(result.notes ?? "");
                changeHasLowTicketCountReference.current.handleClick(result.hasLowTicketCount ?? false);
                changeEventDateReference.current.handleClick(result.eventDate ?? null);
                                
                if (result.venueName) {
                    changeVenueNameReference.current.handleClick(result.venueName ?? null);
                    setVenues([{
                        currentName: result.venueName,
                        venueKey: result.venueKey
                    }]);
                    setVenueName(result.venueName);
                }
                else {
                    changeVenueNameReference.current.handleClick(null);
                    setVenues([]);
                    setVenueName(null);
                }    
                
                let foundStatus = props.venueEventStatusTypes.find(element => element.venueEventStatusTypeId === result.status);
                changeVenueEventStatusTypeReference.current.handleClick(foundStatus);

                if (result.eventParameters) {
                    setEventParameters(result.eventParameters);
                }
                else {
                    setEventParameters([]);
                }
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setGettingEventDetails(false);
            });
    }

    const fiftyCharacterRequiredValidator = (value) => {
        if (!value) {
            return "This field is required";
        }

        if (value.length > 50) {
            return "Maximum length is 50 characters";
        }

        return "";
    }

    const fiftyCharacterValidator = (value) => {
        if (!value) {
            return "";
        }

        if (value.length > 50) {
            return "Maximum length is 50 characters";
        }

        return "";
    }

    const oneHundredCharacterValidator = (value) => {
        if (!value) {
            return "";
        }

        if (value.length > 100) {
            return "Maximum length is 100 characters";
        }

        return "";
    }

    const oneThousandCharacterRequiredValidator = (value) => {
        if (!value) {
            return "This field is required";
        }

        if (value.length > 1000) {
            return "Maximum length is 1,000 characters";
        }

        return "";
    }

    const oneThousandCharacterValidator = (value) => {
        if (!value) {
            return "";
        }

        if (value.length > 1000) {
            return "Maximum length is 1,000 characters";
        }

        return "";
    }

    const requiredValidator = (value) => {
        if (!value) {
            return "This field is required";
        }

        return "";
    }

    const ticketingWebsiteChanged = (event) => {
        setSelectedTicketingWebsiteId(event.target.value.ticketingWebsiteId);
    };

    const ticketingUrlChanged = (event) => {
        setEnteredTicketingUrl(event.target.value);
    }

    const tabStripSelectionChanged = (e) => {
        setSelectedTab(e.selected);
    };

    const venueChanged = (event) => {
        const value = event.value;

        clearTimeout(autocompleteTimeout.current);

        if (!value || value.length < 2) {
            setVenues([]);
            setAreVenuesLoading(false);
            setVenueName(null);
            return;
        }
        
        autocompleteTimeout.current = setTimeout(() => {
            const dataItem = {
                name: value,
                onlyActive: true,
            };

            fetch(`/api/administrator/venues`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        "X-XSRF-TOKEN": getCSRFToken(),
                    },
                    body: JSON.stringify(dataItem),
                })
                .then(handleResponse)
                .then(result => {
                    setVenues(result.venues);  
                    setVenueName(value);
                })
                .catch(error => {
                    addNotificationWithTimeout(dispatch, "error", error.message);
                })
                .finally(() => {
                    setAreVenuesLoading(false);
                });
        }, autocompleteTimeoutDelay);

        setAreVenuesLoading(true);
    };

    const addEventParameterKey = (event) => {
        setEventParameterKey(event.value);
    };

    const addEventParameterValue = (event) => {
        setEventParameterValue(event.value);
    };

    const addEventParameter = () => {
        if (!eventParameterKey
            || !eventParameterValue) {
            addNotificationWithTimeout(dispatch, "error", "The alert parameter key and value are required.");
            return;
        }

        const existingParameter = eventParameters.find(element => element.key === eventParameterKey);

        if (existingParameter) {
            addNotificationWithTimeout(dispatch, "error", "A parameter already exists with that key.");
            return;
        }

        const newEventParameters = [...eventParameters];
        newEventParameters.push({
            key: eventParameterKey,
            value: eventParameterValue
        });
        setEventParameters(newEventParameters);
    };

    const removeEventParameter = (key) => {
        const existingParameter = eventParameters.find(element => element.key === key);

        if (!existingParameter) {
            addNotificationWithTimeout(dispatch, "error", "No parameter found with that key.");
            return;
        }

        const indexOfItem = eventParameters.indexOf(existingParameter);

        const newEventParameters = [...eventParameters];
        newEventParameters.splice(indexOfItem, 1);
        setEventParameters(newEventParameters);
    }

    const EventParameterRender = (props) => {
        const item = props.dataItem;

        return (
            <div className="mb-2">
                {item.key}: {item.value} 
                &nbsp;&nbsp;
                <Button type={"button"} onClick={() => removeEventParameter(item.key)} icon="close" fillMode="outline" />
            </div>
        );
    };

    const closeDialog = () => {
        props.selectVenueEvent(props.venueEvent?.venueEventKey);
        setShowDialog(false);
    };

    const getForm = (venueEvent) => {
        if (!venueEvent) {
            venueEvent = {
                
            };
        }
        else {
            venueEvent.ticketingWebsite = props.ticketingWebsites.find(element => element.ticketingWebsiteId === venueEvent.ticketingWebsiteId);
            venueEvent.venueEventStatusType = props.venueEventStatusTypes.find(element => element.venueEventStatusTypeId === venueEvent.venueEventStatusTypeId);
        }

        if (!venueEvent?.venueEventKey && selectedTab > 0) {
            //Cannot have selected tab be a disabled one
            setSelectedTab(0);
        }
        
        return (
            <div className="administratorVenueEvent">
                <TabStrip selected={selectedTab} onSelect={tabStripSelectionChanged}>
                    <TabStripTab title={"Event"}>
                        <>
                            { gettingEventDetails === true ? <LoadingPanel /> : <></> }
                            <Form
                                onSubmit={handleSubmit}
                                initialValues={venueEvent}
                                key={JSON.stringify(venueEvent)}
                                render={(formRenderProps) => (
                                    <FormElement>
                                        <h2>
                                            {!venueEvent?.venueEventKey ? "New Event" : "Update Event"}
                                        </h2>

                                        {venueEvent?.isEventBeforeShortingCreationDelay ?
                                            <h4 className="mb-0 mt-3 noFormField text-danger">
                                                *** Event is too soon to short ***
                                            </h4>                                            
                                            : (venueEvent?.isEventDuringBlackoutPeriod ? 
                                                <h4 className="mb-0 mt-3 noFormField text-danger">
                                                    *** Event is during blackout period ***
                                                </h4>
                                                : "")
                                        }

                                        {venueEvent?.areThereMultipleEventsOnSameDay ?
                                            <h4 className="mb-0 mt-3 noFormField text-danger">
                                                *** There are multiple events on the same day ***
                                            </h4>
                                            : ""
                                        }

                                        <fieldset className={"k-form-fieldset"}>
                                            {!venueEvent?.venueEventKey || !venueEvent?.ticketingUrl ? "" :
                                                (<div className="mb-3 noFormField">
                                                    <Link to={{ pathname: venueEvent?.ticketingUrl }} target={"_blank"} rel={"noopener noreferrer"}>View Website</Link>
                                                </div>)
                                            }

                                            {!venueEvent?.venueEventKey ? "" :
                                                (<div className="mb-3 noFormField">
                                                    <Label>Key:</Label> {venueEvent.venueEventKey}
                                                </div>)
                                            }

                                            {!venueEvent?.venueEventKey ? "" :
                                                (<div className="mb-3 noFormField">
                                                    <Label>Created:</Label> {venueEvent.dateCreated}
                                                </div>)
                                            }

                                            {!venueEvent?.createdByTicketingWebsiteAlertName ? "" :
                                                (<div className="mb-3 noFormField">
                                                    <Label>Created By Alert:</Label> {venueEvent.createdByTicketingWebsiteAlertName}
                                                </div>)
                                            }

                                            <div className="row">
                                                <div className="col-md-4">
                                                    <Field
                                                        id={"ticketingWebsite"}
                                                        name={"ticketingWebsite"}
                                                        label={"Ticketing website"}
                                                        component={FormDropDownList}
                                                        data={props.ticketingWebsites}
                                                        textField="name"
                                                        dataItemKey="ticketingWebsiteId"
                                                        validator={requiredValidator}
                                                        onChange={ticketingWebsiteChanged}
                                                    /> 

                                                    <Button 
                                                        type="button" 
                                                        className="d-none"
                                                        ref={changeTicketingWebsiteReference}
                                                        onClick={(newValue) => {
                                                            formRenderProps.onChange("ticketingWebsite", { value: newValue });
                                                        }}>Change value</Button>
                                                </div>
                                                <div className="col-md-5">
                                                    <Field
                                                        id={"ticketingUrl"}
                                                        name={"ticketingUrl"}
                                                        label={"Ticketing Url"}
                                                        component={FormInput}
                                                        validator={oneThousandCharacterValidator}
                                                        onChange={ticketingUrlChanged}
                                                    />

                                                    <Button 
                                                        type="button" 
                                                        className="d-none"
                                                        ref={changeTicketingUrlReference}
                                                        onClick={(newValue) => {
                                                            formRenderProps.onChange("ticketingUrl", { value: newValue });
                                                        }}>Change value</Button>
                                                </div>
                                                <div className="col-md-3">
                                                    <div className="k-form-field">
                                                        <Label className="k-label">&nbsp;</Label>

                                                        <Button
                                                            type={"button"}
                                                            onClick={() => getEventDetails()}>
                                                            Get Event Details
                                                        </Button>
                                                    </div>                                                    
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <Field
                                                        id={"ticketingEventId"}
                                                        name={"ticketingEventId"}
                                                        label={"Ticketing Event Id"}
                                                        component={FormInput}
                                                        validator={oneThousandCharacterRequiredValidator}
                                                    />

                                                    <Button 
                                                        type="button" 
                                                        className="d-none"
                                                        ref={changeTicketingEventIdReference}
                                                        onClick={(newValue) => {
                                                            formRenderProps.onChange("ticketingEventId", { value: newValue });
                                                        }}>Change value</Button>
                                                </div>
                                                <div className="col-md-6">
                                                    <Field
                                                        id={"venueName"}
                                                        name={"venueName"}
                                                        label={"Venue"}
                                                        component={FormAutoComplete}
                                                        data={venues}        
                                                        placeholder="e.g. Roseland"
                                                        textField="currentName"
                                                        onChange={venueChanged}
                                                        loading={areVenuesLoading}
                                                        validator={requiredValidator}
                                                    />

                                                    <Button 
                                                        type="button" 
                                                        className="d-none"
                                                        ref={changeVenueNameReference}
                                                        onClick={(newValue) => {
                                                            formRenderProps.onChange("venueName", { value: newValue });
                                                        }}>Change value</Button>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <Field
                                                        id={"venueEventStatusType"}
                                                        name={"venueEventStatusType"}
                                                        label={"Status"}
                                                        component={FormDropDownList}
                                                        data={props.venueEventStatusTypes}
                                                        textField="name"
                                                        dataItemKey="venueEventStatusTypeId"
                                                        validator={requiredValidator}
                                                    /> 

                                                    <Button 
                                                        type="button" 
                                                        className="d-none"
                                                        ref={changeVenueEventStatusTypeReference}
                                                        onClick={(newValue) => {
                                                            formRenderProps.onChange("venueEventStatusType", { value: newValue });
                                                        }}>Change value</Button>
                                                </div>
                                                <div className="col-md-6">
                                                    <Field
                                                        id={"eventDate"}
                                                        name={"eventDate"}
                                                        label={"Event date"}
                                                        component={FormInput}
                                                        validator={fiftyCharacterRequiredValidator}
                                                    />

                                                    <Button 
                                                        type="button" 
                                                        className="d-none"
                                                        ref={changeEventDateReference}
                                                        onClick={(newValue) => {
                                                            formRenderProps.onChange("eventDate", { value: newValue });
                                                        }}>Change value</Button>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-4">
                                                    <Field
                                                        id={"tagline"}
                                                        name={"tagline"}
                                                        label={"Tagline"}
                                                        component={FormInput}
                                                        validator={oneHundredCharacterValidator}
                                                    />

                                                    <Button 
                                                        type="button" 
                                                        className="d-none"
                                                        ref={changeTaglineReference}
                                                        onClick={(newValue) => {
                                                            formRenderProps.onChange("tagline", { value: newValue });
                                                        }}>Change value</Button>
                                                </div>
                                                <div className="col-md-4">
                                                    <Field
                                                        id={"header"}
                                                        name={"header"}
                                                        label={"Header"}
                                                        component={FormInput}
                                                        validator={oneThousandCharacterRequiredValidator}
                                                    />

                                                    <Button 
                                                        type="button" 
                                                        className="d-none"
                                                        ref={changeHeaderReference}
                                                        onClick={(newValue) => {
                                                            formRenderProps.onChange("header", { value: newValue });
                                                        }}>Change value</Button>
                                                </div>
                                                <div className="col-md-4">
                                                    <Field
                                                        id={"subheader"}
                                                        name={"subheader"}
                                                        label={"Subheader"}
                                                        component={FormInput}
                                                        validator={oneThousandCharacterValidator}
                                                    />

                                                    <Button 
                                                        type="button" 
                                                        className="d-none"
                                                        ref={changeSubheaderReference}
                                                        onClick={(newValue) => {
                                                            formRenderProps.onChange("subheader", { value: newValue });
                                                        }}>Change value</Button>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-4">
                                                    <Field
                                                        id={"purchaseTicketLimit"}
                                                        name={"purchaseTicketLimit"}
                                                        label={"Purchase ticket limit"}
                                                        component={FormNumericTextBox}
                                                        spinners={false}
                                                        format="n0"
                                                    />

                                                    <Button 
                                                        type="button" 
                                                        className="d-none"
                                                        ref={changePurchaseTicketLimitReference}
                                                        onClick={(newValue) => {
                                                            formRenderProps.onChange("purchaseTicketLimit", { value: newValue });
                                                        }}>Change value</Button>
                                                </div>
                                                <div className="col-md-4">
                                                    <Field
                                                        id={"minimumAge"}
                                                        name={"minimumAge"}
                                                        label={"Minimum age"}
                                                        component={FormNumericTextBox}
                                                        spinners={false}
                                                        format="n0"
                                                    />

                                                    <Button 
                                                        type="button" 
                                                        className="d-none"
                                                        ref={changeMinimumAgeReference}
                                                        onClick={(newValue) => {
                                                            formRenderProps.onChange("minimumAge", { value: newValue });
                                                        }}>Change value</Button>
                                                </div>
                                                <div className="col-md-4">
                                                    <Field
                                                        id={"capacity"}
                                                        name={"capacity"}
                                                        label={"Capacity"}
                                                        component={FormNumericTextBox}
                                                        spinners={false}
                                                        format="n0"
                                                    />

                                                    <Button 
                                                        type="button" 
                                                        className="d-none"
                                                        ref={changeCapacityReference}
                                                        onClick={(newValue) => {
                                                            formRenderProps.onChange("capacity", { value: newValue });
                                                        }}>Change value</Button>
                                                </div>
                                            </div>                                            
                                            <div className="row">
                                                <div className="col-md-5">
                                                    <Field
                                                        id={"notes"}
                                                        name={"notes"}
                                                        label={"Notes"}
                                                        component={FormInput}
                                                        validator={oneThousandCharacterValidator}
                                                    />

                                                    <Button 
                                                        type="button" 
                                                        className="d-none"
                                                        ref={changeNotesReference}
                                                        onClick={(newValue) => {
                                                            formRenderProps.onChange("notes", { value: newValue });
                                                        }}>Change value</Button>
                                                </div>
                                                <div className="col-md-2">
                                                    <Field
                                                        id={"posEventId"}
                                                        name={"posEventId"}
                                                        label={"POS Event Id"}
                                                        component={FormInput}
                                                        validator={fiftyCharacterValidator}
                                                    />
                                                </div>
                                                <div className="col-md-1">
                                                    <div className="k-form-field">
                                                        <Label className="k-label">&nbsp;</Label>

                                                        {!venueEvent?.venueEventKey ? null :
                                                            (<Button
                                                                type={"button"}
                                                                onClick={() => setShowDialog(true)}>
                                                                Match
                                                            </Button>
                                                        )}                                                    
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="k-form-field">
                                                        <Label>POS Event Name</Label> {venueEvent?.posEventName}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <Field
                                                        id={"hasLowTicketCount"}
                                                        name={"hasLowTicketCount"}
                                                        label={"Has low ticket count"}
                                                        component={FormCheckbox}
                                                    />

                                                    <Button 
                                                        type="button" 
                                                        className="d-none"
                                                        ref={changeHasLowTicketCountReference}
                                                        onClick={(newValue) => {
                                                            formRenderProps.onChange("hasLowTicketCount", { value: newValue });
                                                        }}>Change value</Button>
                                                </div>
                                                <div className="col-md-6">
                                                    <Field
                                                        id={"isPurchaseRestrictedByLocation"}
                                                        name={"isPurchaseRestrictedByLocation"}
                                                        label={"Is purchase restricted by location"}
                                                        component={FormCheckbox}
                                                    />

                                                    <Button 
                                                        type="button" 
                                                        className="d-none"
                                                        ref={changeIsPurchaseRestrictedByLocationReference}
                                                        onClick={(newValue) => {
                                                            formRenderProps.onChange("isPurchaseRestrictedByLocation", { value: newValue });
                                                        }}>Change value</Button>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="k-form-field">
                                                        <Label className="k-label">
                                                            Alert Parameters
                                                        </Label>
                                                        <div className="k-form-field-wrap">
                                                            <div className="row">
                                                                <div className="col-md-5">
                                                                    <Input id={"eventParameterKey"} onChange={addEventParameterKey} placeholder="Key" />
                                                                </div>
                                                                <div className="col-md-5">
                                                                    <Input id={"eventParameterValue"} onChange={addEventParameterValue} placeholder="Value" />
                                                                </div>
                                                                <div className="col-md-2">
                                                                    <Button 
                                                                        type="button" 
                                                                        themeColor={"primary"}                                                         
                                                                        onClick={addEventParameter}>
                                                                        Add
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="k-form-field">
                                                        <Label className="k-label">
                                                            &nbsp;
                                                        </Label>
                                                        <div className="k-form-field-wrap">                                                            
                                                            <ListView
                                                                data={eventParameters}
                                                                item={EventParameterRender}
                                                                style={{
                                                                    width: "100%",
                                                                }}
                                                                />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </fieldset>
                                        <span className={"k-form-separator"} />
                                        <div className="k-form-buttons">
                                            <Button
                                                themeColor={"primary"}
                                                type={"submit"}
                                                disabled={!formRenderProps.allowSubmit}
                                                >
                                                Save
                                            </Button>

                                            {!venueEvent?.venueEventKey ? null :
                                                (<Button
                                                    type={"button"}
                                                    className={"deleteButton"}
                                                    onClick={() => deleteVenueEvent(venueEvent.venueEventKey)}>
                                                    Delete
                                                </Button>
                                            )}
                                        </div>
                                    </FormElement>
                                )}
                            />
                        </>
                    </TabStripTab> 
                    <TabStripTab title={"Ticket Types"} disabled={!venueEvent?.venueEventKey}>
                        <AdministratorVenueEventTicketTypes venueEventKey={venueEvent?.venueEventKey} />
                    </TabStripTab>
                    <TabStripTab title={"Counts"} disabled={!venueEvent?.venueEventKey}>
                        <AdministratorVenueEventTicketCounts venueEventKey={venueEvent?.venueEventKey} />
                    </TabStripTab>
                    <TabStripTab title={"Alerts"} disabled={!venueEvent?.venueEventKey}>
                        <AdministratorVenueEventAlerts venueEventKey={venueEvent?.venueEventKey} ticketingWebsiteId={venueEvent?.ticketingWebsiteId} />
                    </TabStripTab>
                    <TabStripTab title={"Shorting"} disabled={!venueEvent?.venueEventKey}>
                        <AdministratorVenueEventShorting venueEventKey={venueEvent?.venueEventKey} />
                    </TabStripTab>
                    <TabStripTab title={"Performers"} disabled={!venueEvent?.venueEventKey}>
                        <AdministratorVenueEventPerformers venueEventKey={venueEvent?.venueEventKey} />
                    </TabStripTab>
                    <TabStripTab title={"Statuses"} disabled={!venueEvent?.venueEventKey}>
                        <AdministratorVenueEventStatuses venueEventKey={venueEvent?.venueEventKey} />
                    </TabStripTab>                    
                    <TabStripTab title={"Logs"} disabled={!venueEvent?.venueEventKey}>
                        <AdministratorVenueEventLogs venueEventKey={venueEvent?.venueEventKey} />
                    </TabStripTab>
                    <TabStripTab title={"Report Hiding"} disabled={!venueEvent?.venueEventKey}>
                        <AdministratorVenueEventHiddenFromReports venueEventKey={venueEvent?.venueEventKey} />
                    </TabStripTab>  
                    <TabStripTab title={"Temporary Report Hiding"} disabled={!venueEvent?.venueEventKey}>
                        <AdministratorVenueEventTemporarilyHiddenFromReports venueEventKey={venueEvent?.venueEventKey} />
                    </TabStripTab>  
                    <TabStripTab title={"Blocked Websites"} disabled={!venueEvent?.venueEventKey}>
                        <AdministratorVenueEventBlockedSecondaryTicketingWebsites venueEventKey={venueEvent?.venueEventKey} />
                    </TabStripTab>
                </TabStrip>
                <div className="administratorVenueEventClose">
                    <Button
                        themeColor={"primary"}
                        type={"button"}
                        onClick={props.cancel}
                    >
                        Close
                    </Button>
                </div>
            </div>
        );
    }

    useEffect(() => {
        setEventParameters(props.venueEvent?.venueEventParameters ?? []);
        setEnteredTicketingUrl(props.venueEvent?.ticketingUrl ?? null);
        setSelectedTicketingWebsiteId(props.venueEvent?.ticketingWebsiteId ?? null);
        setVenueName(props.venueEvent?.venueName ?? null);

        if (props.venueEvent) {
            setVenues([{
                currentName: props.venueEvent.venueName,
                venueKey: props.venueEvent.venueKey
            }]);
        }
        else {
            setVenues([]);
        }

    }, [props]);

    return (
        <>
            { props.loading === true ? <LoadingPanel /> : (props.createNewVenueEvent === true ? getForm() : (props.venueEvent ? getForm(props.venueEvent) : <div></div>))}

            {showDialog === false ? <></> :
                <Dialog
                    title={"Match Events" + (props.venueEvent?.header ? ` - ${props.venueEvent?.header}` : "")}
                    onClose={() => setShowDialog(false)}
                    width={"90vw"}
                >
                    <AdministratorMatchVenueEvent
                        venueEventKey={props.venueEvent?.venueEventKey} 
                        closeDialog={closeDialog} />
                </Dialog>
            }
        </>
    );
}